import Authenticated from 'components/auth/Authenticated';
import SchoolJobSection from 'components/jobs/SchoolJobsSection';
import CommonLayout from 'components/layouts/common';
import MultipleMarkerMap from 'components/locations/multipleMarkerMap';
import Card from 'components/sections/Card';
import Head from 'next/head';
import { withRouter } from 'next/router';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { fetchEducationalQualifications } from 'redux/educational_qualifications';
import { fetchEmployer } from 'redux/employer/actions';
import toSentence from 'util/formatting/sentence';
import { externalUrl } from 'util/formatting/url';
import CreateConversationButton from '../../components/messages/CreateConversationButton';
import { useUser } from 'hooks/redux/useUser';
import { AlertIcon } from 'icons/outline';
import { Banner } from 'components/Banner';
import { useAccount } from 'hooks/redux/useAccount';
import { Avatar } from '../../components/Avatar';
import { SchoolGraphic } from '../../graphics';
import { LoadingImage } from 'components/LoadingImage';

function EmployerProfileShow(props) {
  const { employer } = props;
  const { id } = props.router.query;

  const { currentUserProfile } = useUser();
  const { authenticated, loaded } = useAccount();
  const isEmployer = !currentUserProfile?.id && authenticated;
  const viewedByLoggedOut = !authenticated && loaded;
  const viewedByTeacher = currentUserProfile?.id && authenticated;

  useEffect(() => {
    if (id) {
      if (viewedByLoggedOut || viewedByTeacher) {
        props.dispatch(fetchEmployer(id));
      }
      props.dispatch(fetchEducationalQualifications());
    }
  }, [id, viewedByLoggedOut, viewedByTeacher]);

  if (!Object.keys(props.router.query).length) return null;

  const locations = employer?.locations?.().toArray();

  return (
    <Authenticated redirectLoggedOut={false} employer={isEmployer}>
      <CommonLayout>
        <Head>
          <title>{employer.name} - EarlyDay.com</title>
        </Head>
        {/* <TeacherLayout> */}
        {props.loading ? (
          <div className="flex items-center justify-center h-[calc(100vh_-_130px)]">
            <LoadingImage src="/images/loading/InternalLoadingSchools.gif" />
          </div>
        ) : (
          <div className="flex h-full pt-8">
            {/* Main column */}
            <div className="flex flex-col flex-1 w-0 overflow-hidden">
              <main className="relative z-0 flex-1 overflow-y-auto focus:outline-none">
                {(isEmployer || null) && (
                  <div className="mb-6">
                    <Banner role="note" variant="outline" intent="warning">
                      <div className="flex items-start justify-start gap-4">
                        <AlertIcon className="flex-none w-5 h-5" />
                        <p className="text-sm">
                          This is a preview mode of the {employer?.name} profile. Logged in teachers
                          can click "Send Message" to start a conversation with this employer.
                        </p>
                      </div>
                    </Banner>
                  </div>
                )}

                {/* Page title & actions */}
                <Card>
                  <div className="px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8">
                    <div className="flex-1 min-w-0">
                      <div className="grid grid-cols-12 gap-4 pb-8 align-middle">
                        <div className="relative w-auto col-span-12 md:col-span-2 md:block h-36 md:h-48">
                          <Avatar
                            name={employer.name}
                            variant="circle"
                            imageProps={{
                              src: employer.profileImage?.downloadUrl,
                              objectFit: 'contain',
                              alt: employer.name,
                            }}
                            backgroundClass="bg-secondary-blossom"
                            className="w-36 h-36 md:w-48 md:h-48"
                          >
                            <SchoolGraphic className="w-4/5 h-4/5" />
                          </Avatar>
                        </div>
                        <div className="self-center col-span-10 pl-8">
                          <div className="flex items-center justify-center space-x-3 md:justify-start">
                            <h1 className="text-xl font-medium truncate md:pl-4">
                              {employer?.name}
                            </h1>
                          </div>
                        </div>
                      </div>
                      <div className="float-right col-span-2 pl-8">
                        <CreateConversationButton
                          employer={employer}
                          className="flex-shrink-0"
                          posthog={{
                            eventName: 'Click Send Message on Employer Page',
                          }}
                          size="lg"
                          disabled={isEmployer}
                        />
                      </div>
                      {employer.website && (
                        <div className="pt-4">
                          <p className="text-lg font-bold leading-5">Website</p>
                          <p className="mt-4 font-medium sm:truncate">
                            <a
                              className="text-primary-pure-blue"
                              href={externalUrl(employer.website)}
                              rel="noopener"
                            >
                              {employer.website}
                            </a>
                          </p>
                        </div>
                      )}
                      {employer.about && (
                        <div className="py-4">
                          <p className="text-lg font-bold leading-5">About</p>
                          <p className="mt-4 font-medium ">{employer.about}</p>
                        </div>
                      )}

                      {!!employer.educationPhilosophy?.length && (
                        <div className="py-4">
                          <p className="text-lg font-bold leading-5">Education Philosophy</p>
                          <p className="mt-4 font-medium sm:truncate">
                            {toSentence(employer.educationPhilosophy)}
                          </p>
                        </div>
                      )}

                      {!!employer.languages?.length && (
                        <div className="py-4">
                          <p className="text-lg font-bold leading-5">Languages</p>
                          <p className="mt-4 font-medium sm:truncate">
                            {toSentence(employer.languages)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8">
                    <div className="flex-1 min-w-0">
                      <h1 className="py-4 text-lg font-medium leading-6 text-gray-900 sm:truncate">
                        Locations
                      </h1>
                      <MultipleMarkerMap {...{ locations }} />
                    </div>
                  </div>
                </Card>
                {employer.id && (
                  <Card>
                    <SchoolJobSection employer={employer} />
                  </Card>
                )}
              </main>
            </div>
          </div>
        )}
        {/* </TeacherLayout> */}
      </CommonLayout>
    </Authenticated>
  );
}

function mapStateToProps(state) {
  return {
    employer: state.employer.employer,
    loading: state.employer.loading,
  };
}

export default connect(mapStateToProps)(withRouter(EmployerProfileShow));
